import React, { useEffect, useState, useCallback } from 'react';
import { Layout, Menu, Button, Table, Space, Modal, Form, Input, message, Typography, Popconfirm, Tabs, Badge, Radio, Drawer } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/useAuth';
import { useTeachers } from '../services/useTeachers';
import { useParentRegistrations, ParentRegistration } from '../services/useParentRegistrations';
import './AdminDashboard.css';
import '../styles/common.css';
import {
  UserOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  InfoCircleOutlined,
  EditOutlined,
  KeyOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

// Define Breakpoint type locally instead of importing
type Breakpoint = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

// Remove FixedType import and define it locally
type FixedType = 'left' | 'right' | boolean;

const AdminDashboard: React.FC = () => {
  const { logout, username, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { 
    teachers, 
    loading, 
    fetchTeachers, 
    createTeacher, 
    deleteTeacher,
    updateTeacher 
  } = useTeachers();
  const {
    pendingRegistrations,
    registrations,
    loading: loadingRegistrations,
    reviewing,
    fetchAllRegistrations,
    fetchPendingRegistrations,
    reviewRegistration
  } = useParentRegistrations();
  
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState<any>(null);
  const [currentRegistration, setCurrentRegistration] = useState<ParentRegistration | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [reviewForm] = Form.useForm();
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<string>('teachers');
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const pageSize = 10;

  // 处理侧边栏折叠
  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerVisible(!drawerVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // 处理菜单点击，在移动端自动关闭抽屉
  const handleMenuClick = (key: string) => {
    setActiveTab(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // 响应窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };

    // 初始检查
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 给状态一点时间稳定
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialized(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // 安全地加载教师列表
  const safelyFetchTeachers = useCallback(async () => {
    try {
      console.log('开始加载教师列表...');
      setLoadingTeachers(true);
      await fetchTeachers();
      console.log('教师列表加载完成');
    } catch (error: any) {
      console.error('加载教师列表出错:', error);
      message.error(error?.message || '加载数据失败，请刷新页面重试');
    } finally {
      setLoadingTeachers(false);
    }
  }, [fetchTeachers]);

  // 只在组件初始化且登录状态稳定后加载数据
  useEffect(() => {
    if (!initialized) return;
    
    if (isLoggedIn) {
      console.log('AdminDashboard: 用户已登录，正在加载教师列表');
      safelyFetchTeachers();
      
      // 加载家长注册申请数据
      fetchPendingRegistrations();
      fetchAllRegistrations();
    }
  }, [isLoggedIn, initialized, safelyFetchTeachers, fetchPendingRegistrations, fetchAllRegistrations]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 这些点击事件处理会将用户导航到子路由
  const handleViewBasicInfo = (teacherId: number) => {
    console.log(`AdminDashboard: 导航到教师 ${teacherId} 的基本信息`);
    // 使用replace: false确保不会替换历史记录，方便用户返回
    navigate(`/admin/teachers/${teacherId}/basic-info`, { replace: false });
  };

  const handleViewDetailedInfo = (teacherId: number) => {
    console.log(`AdminDashboard: 导航到教师 ${teacherId} 的详细信息`);
    // 使用replace: false确保不会替换历史记录，方便用户返回
    navigate(`/admin/teachers/${teacherId}/detailed-info`, { replace: false });
  };

  // 如果用户在加载过程中被登出，不立即重定向
  // 交给ProtectedRoute处理重定向，避免竞争条件
  if (initialized && !isLoggedIn) {
    console.log('AdminDashboard: 用户未登录，但不直接重定向');
    return null;
  }

  const handleCreateTeacher = async (values: any) => {
    const success = await createTeacher(values);
    if (success) {
      setIsCreateModalVisible(false);
      form.resetFields();
    }
  };

  const handleUpdateTeacher = async (values: any) => {
    if (!currentTeacher) return;
    
    // 如果密码为空，则从请求中移除密码字段
    if (values.password === '') {
      delete values.password;
    }
    
    const success = await updateTeacher(currentTeacher.id, values);
    if (success) {
      setIsUpdateModalVisible(false);
      updateForm.resetFields();
      setCurrentTeacher(null);
    }
  };

  const showUpdateModal = (teacher: any) => {
    setCurrentTeacher(teacher);
    updateForm.setFieldsValue({
      username: teacher.username,
      password: '' // 密码字段置空，需要重新设置
    });
    setIsUpdateModalVisible(true);
  };

  const handleDeleteTeacher = async (id: number) => {
    const success = await deleteTeacher(id);
    if (success) {
      message.success('教师删除成功');
      
      // 计算当前页面应该显示的教师数
      const totalItems = teachers.length - 1; // 减去被删除的一个
      const totalPages = Math.ceil(totalItems / pageSize);
      
      // 如果当前页已经没有记录，且不是第一页，则切换到上一页
      if (currentPage > totalPages && currentPage > 1) {
        setCurrentPage(totalPages);
      }
    }
  };

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      className: 'serial-number-cell',
      render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      responsive: ['md' as Breakpoint]
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        <span className={active ? "status-active" : "status-inactive"}>
          {active ? '活跃' : '禁用'}
        </span>
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: isMobile ? 'right' as FixedType : undefined,
      render: (_: any, record: any) => (
        <Space size="middle" wrap={isMobile} direction={isMobile ? "vertical" : "horizontal"}>
          <Button 
            type="primary"
            icon={<InfoCircleOutlined />}
            onClick={() => handleViewBasicInfo(record.id)}
            size={isMobile ? "small" : "middle"}
          >
            基本信息
          </Button>
          <Button 
            icon={<EditOutlined />}
            onClick={() => handleViewDetailedInfo(record.id)}
            size={isMobile ? "small" : "middle"}
          >
            详细信息
          </Button>
          <Button 
            icon={<KeyOutlined />}
            onClick={() => showUpdateModal(record)}
            size={isMobile ? "small" : "middle"}
          >
            修改账户
          </Button>
          <Popconfirm
            title="确定要删除此教师吗？"
            onConfirm={() => handleDeleteTeacher(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />}
              size={isMobile ? "small" : "middle"}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 显示审核modal
  const showReviewModal = (registration: ParentRegistration) => {
    setCurrentRegistration(registration);
    reviewForm.resetFields();
    setIsReviewModalVisible(true);
  };

  // 处理审核
  const handleReviewRegistration = async (values: any) => {
    if (!currentRegistration) return;
    
    const success = await reviewRegistration(
      currentRegistration.id,
      values.status,
      values.reviewRemark
    );
    
    if (success) {
      setIsReviewModalVisible(false);
      reviewForm.resetFields();
      setCurrentRegistration(null);
      
      // 刷新数据
      fetchPendingRegistrations();
      fetchAllRegistrations();
    }
  };

  // 家长注册申请列表列定义
  const registrationColumns = [
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: '申请备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      width: 300,
      responsive: ['md' as Breakpoint]
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md' as Breakpoint],
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (status === 'PENDING') return <Badge status="processing" text="待审核" />;
        if (status === 'APPROVED') return <Badge status="success" text="已通过" />;
        if (status === 'REJECTED') return <Badge status="error" text="已拒绝" />;
        return <Badge status="default" text="未知" />;
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: isMobile ? 'right' as FixedType : undefined,
      render: (_: any, record: ParentRegistration) => (
        <Space size="middle" wrap={isMobile} direction={isMobile ? "vertical" : "horizontal"}>
          {record.status === 'PENDING' && (
            <Button 
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={() => showReviewModal(record)}
              size={isMobile ? "small" : "middle"}
            >
              审核
            </Button>
          )}
          {record.status !== 'PENDING' && (
            <Text type="secondary">已审核</Text>
          )}
        </Space>
      ),
    },
  ];

  // 所有家长注册申请列表列定义
  const allRegistrationColumns = [
    ...registrationColumns.slice(0, 4),
    {
      title: '审核时间',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      responsive: ['md' as Breakpoint],
      render: (date: string) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: '审核人',
      dataIndex: 'reviewedByAdmin',
      key: 'reviewedByAdmin',
      responsive: ['md' as Breakpoint],
      render: (admin: string) => admin || '-'
    },
    {
      title: '审核备注',
      dataIndex: 'reviewRemark',
      key: 'reviewRemark',
      ellipsis: true,
      responsive: ['md' as Breakpoint],
      render: (remark: string) => remark || '-'
    }
  ];

  // 教师管理Tab内容
  const renderTeachersTab = () => (
    <div className="content-section">
      <div className="section-header">
        <Title level={isMobile ? 5 : 4}>教师管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setIsCreateModalVisible(true);
          }}
          size={isMobile ? "small" : "middle"}
        >
          添加教师
        </Button>
      </div>
      
      <div className="overflow-auto">
        <Table
          dataSource={teachers}
          columns={columns}
          rowKey="id"
          loading={loadingTeachers}
          pagination={{
            pageSize,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          scroll={isMobile ? { x: 800 } : undefined}
          className="responsive-table"
        />
      </div>
    </div>
  );

  // 家长注册审核Tab内容
  const renderParentRegistrationsTab = () => (
    <div className="content-section">
      <div className="section-header">
        <Title level={isMobile ? 5 : 4}>家长注册申请审核</Title>
        <Badge count={pendingRegistrations.length} style={{ backgroundColor: '#108ee9' }}>
          <Button 
            type="primary" 
            onClick={fetchPendingRegistrations}
            size={isMobile ? "small" : "middle"}
          >
            刷新
          </Button>
        </Badge>
      </div>
      
      <Tabs defaultActiveKey="pending" className="registration-tabs">
        <TabPane tab="待审核申请" key="pending">
          <div className="overflow-auto">
            <Table
              dataSource={pendingRegistrations}
              columns={registrationColumns}
              rowKey="id"
              loading={loadingRegistrations}
              pagination={{
                pageSize,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 条记录`
              }}
              scroll={isMobile ? { x: 600 } : undefined}
              className="responsive-table"
            />
          </div>
        </TabPane>
        <TabPane tab="所有申请" key="all">
          <div className="overflow-auto">
            <Table
              dataSource={registrations}
              columns={allRegistrationColumns}
              rowKey="id"
              loading={loadingRegistrations}
              pagination={{
                pageSize,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 条记录`
              }}
              scroll={isMobile ? { x: 800 } : undefined}
              className="responsive-table"
            />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );

  // 侧边栏菜单
  const sideMenu = (
    <Menu
      mode="inline"
      selectedKeys={[activeTab]}
      defaultSelectedKeys={['teachers']}
      style={{ height: '100%' }}
      onClick={({ key }) => handleMenuClick(key as string)}
    >
      <Menu.Item key="teachers" icon={<UserOutlined />}>
        教师管理
      </Menu.Item>
      <Menu.Item key="parent-registrations" icon={<UserSwitchOutlined />}>
        <Badge count={pendingRegistrations.length} offset={[10, 0]}>
          家长注册审核
        </Badge>
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout className="layout-container">
      <Header className="header-container">
        <div className="header-title-container">
          <Button 
            type="text" 
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)} 
            onClick={toggleSidebar}
            className="header-button"
          />
          DMTeachers 管理系统
        </div>
        <div className="user-info-container">
          <span className="margin-right-std">{username}</span>
          <Button 
            type="link" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
          >
            登出
          </Button>
        </div>
      </Header>
      
      <Layout>
        {!isMobile && (
          <Sider
            width={200}
            collapsible
            collapsed={collapsed}
            trigger={null}
            className="sidebar"
          >
            {sideMenu}
          </Sider>
        )}
        
        <Content className="content-container">
          {activeTab === 'teachers' && renderTeachersTab()}
          {activeTab === 'parent-registrations' && renderParentRegistrationsTab()}
        </Content>
      </Layout>

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          title={
            <div className="drawer-header">
              <Title level={5} className="margin-bottom-0">
                DMTeachers 管理系统
              </Title>
            </div>
          }
          headerStyle={{ padding: '16px', backgroundColor: '#001529', color: 'white' }}
          bodyStyle={{ padding: 0 }}
          className="mobile-drawer"
        >
          {sideMenu}
        </Drawer>
      )}
      
      {/* 添加教师的Modal */}
      <Modal
        title="添加教师"
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTeacher}
        >
          <Form.Item
            name="name"
            label="教师姓名"
            rules={[{ required: true, message: '请输入教师姓名' }]}
          >
            <Input placeholder="请输入教师姓名" />
          </Form.Item>
          
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>
          
          <Form.Item className="modal-footer">
            <Button onClick={() => setIsCreateModalVisible(false)} className="margin-right-std">
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              添加
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 修改教师账户的Modal */}
      <Modal
        title="修改教师账户"
        visible={isUpdateModalVisible}
        onCancel={() => setIsUpdateModalVisible(false)}
        footer={null}
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form
          form={updateForm}
          layout="vertical"
          onFinish={handleUpdateTeacher}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入新的用户名" />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="密码"
            rules={[{ message: '如需修改密码，请输入新密码' }]}
          >
            <Input.Password placeholder="留空表示不修改密码" />
          </Form.Item>
          
          <Form.Item className="modal-footer">
            <Button onClick={() => setIsUpdateModalVisible(false)} className="margin-right-std">
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              更新
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 审核家长注册申请的Modal */}
      <Modal
        title="审核家长注册申请"
        visible={isReviewModalVisible}
        onCancel={() => setIsReviewModalVisible(false)}
        footer={null}
        maskClosable={false}
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form
          form={reviewForm}
          layout="vertical"
          onFinish={handleReviewRegistration}
        >
          {currentRegistration && (
            <div className="registration-info margin-bottom-std">
              <p><strong>手机号:</strong> {currentRegistration.phone}</p>
              <p><strong>申请备注:</strong> {currentRegistration.remark}</p>
              <p><strong>申请时间:</strong> {new Date(currentRegistration.createdAt).toLocaleString()}</p>
            </div>
          )}
          
          <Form.Item
            name="status"
            label="审核结果"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Radio.Group>
              <Radio.Button value="APPROVED">
                <CheckCircleOutlined /> 通过
              </Radio.Button>
              <Radio.Button value="REJECTED" style={{ marginLeft: 8 }}>
                <CloseCircleOutlined /> 拒绝
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            name="reviewRemark"
            label="审核备注"
          >
            <TextArea rows={3} placeholder="请输入审核备注（选填）" />
          </Form.Item>
          
          <Form.Item className="modal-footer">
            <Button onClick={() => setIsReviewModalVisible(false)} className="margin-right-std">
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={reviewing}>
              提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminDashboard;